import { callExternalApi } from '../../callExternalApi';

export type RiskTierName = 'green' | 'yellow' | 'red';
export type RiskTierDirection = 'past' | 'before';

export type RiskDateFieldSource = 'asset' | 'custom';

export interface RiskDateFieldOption {
  id: string;
  label: string;
  source: RiskDateFieldSource;
}

// available turns false once the account can no longer offer the field; label then falls back to the raw id.
export interface RiskDateFieldRef {
  id: string;
  label: string;
  available: boolean;
}

export interface ProjectManagementConfigTier {
  tier: RiskTierName;
  label: string;
  dayThresholdMin: number | null;
  dayThresholdMax: number | null;
  direction: RiskTierDirection;
  // Optional for the same reason subjectLineFields is: an API deployed before the pair existed sends neither.
  primaryDateField?: RiskDateFieldRef;
  comparisonDateField?: RiskDateFieldRef;
}

export type ConfigSource = 'account' | 'parent' | 'default';

export interface SubjectLineField {
  id: string;
  label: string;
}

// Resolves independently of the tiers — an account can own one and inherit the other.
export interface SubjectLineFieldSelection {
  fields: string[];
  source: ConfigSource;
  // True for `default` as well as `parent`, so it cannot stand in for "inherited from a parent account".
  inherited: boolean;
}

export interface ProjectManagementConfig {
  accountId: string;
  configType: string;
  source: ConfigSource;
  inherited: boolean;
  tiers: ProjectManagementConfigTier[];
  // Optional because the web app can reach an API deployed before the selection existed.
  subjectLineFields?: SubjectLineFieldSelection;
}

export interface ProjectManagementConfigTierUpdate {
  tier: RiskTierName;
  label: string;
  day_threshold_min: number | null;
  day_threshold_max: number | null;
  direction: RiskTierDirection;
  // Omitting either key leaves that tier's stored date alone.
  primary_date_field?: string;
  comparison_date_field?: string;
}

export const getProjectManagementConfig = async (accountId: string, configType = 'risk') => {
  return await callExternalApi<ProjectManagementConfig>({
    url: `api/admin/project_management_configs?account_id=${encodeURIComponent(accountId)}&config_type=${configType}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getSubjectLineFields = async () => {
  return await callExternalApi<{ fields: SubjectLineField[] }>({
    url: 'api/admin/project_management_configs/subject_line_fields',
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

// Account-scoped, unlike the subject line catalog: the account's own date custom fields complete it.
export const getRiskDateFields = async (accountId: string) => {
  return await callExternalApi<{ fields: RiskDateFieldOption[] }>({
    url: `api/admin/project_management_configs/date_fields?account_id=${encodeURIComponent(accountId)}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export interface PutProjectManagementConfigOptions {
  subjectLineFields?: string[];
  configType?: string;
}

export const putProjectManagementConfig = async (
  accountId: string,
  tiers: ProjectManagementConfigTierUpdate[],
  { subjectLineFields, configType = 'risk' }: PutProjectManagementConfigOptions = {}
) => {
  return await callExternalApi<ProjectManagementConfig>({
    url: 'api/admin/project_management_configs',
    method: 'PUT',
    headers: {
      'content-type': 'application/json'
    },
    // Omitting the key leaves the stored selection alone; sending [] is an explicit reset to Address.
    data: {
      account_id: accountId,
      config_type: configType,
      tiers,
      ...(subjectLineFields && { subject_line_fields: subjectLineFields })
    }
  });
};
